// @flow
import { useMemo } from 'react';
import Polygon from 'ol/geom/Polygon';
import { Style, Text, Fill, Stroke, Icon } from 'ol/style';
import { ZINDEX } from '../../../constants/appConstants';
import type { Geometry, Point } from '../../ui/openlayers';
import { useBaseResolution, useLabelConfig } from './styleContext';

export const LABELS_PER_COL = 8;
export const LABEL_WIDTH = 150;
export const LABEL_HEIGHT = 20;
export const LABEL_BOTTOM_MARGIN = 10;
export const LABEL_RIGHT_MARGIN = 10;

export const makeRectangleFromPoint = (point: Point, resolution: number, width: number, height: number, offsetX?: number = 0, offsetY?: number = 0) => {
    if(!width) {
        throw new Error('Width is undefined');
    }
    if(!height) {
        throw new Error('Height is undefined');
    }

    const [x, y] = point.getCoordinates();
    width *= resolution;
    height *= resolution;
    offsetX *= resolution;
    offsetY *= resolution;

    const topLeft = [
        x + (width / 2) + offsetX,
        y - (height / 2) - offsetY
    ];
    const topRight = [
        x - (width / 2) + offsetX,
        y - (height / 2) - offsetY
    ];
    const bottomRight = [
        x - (width / 2) + offsetX,
        y + (height / 2) - offsetY
    ];
    const bottomLeft = [
        x + (width / 2) + offsetX,
        y + (height / 2) - offsetY
    ];

    return new Polygon([[topLeft, topRight, bottomRight, bottomLeft]]);
};

type Cluster = {
    index: number,
    size: number,
    point: Point
};
export const getClusterLabel = (cluster: Cluster, resolution: number) => {
    const colCount = Math.ceil((cluster.size - 1) / LABELS_PER_COL);
    const clusterHeight = (Math.min(cluster.size, LABELS_PER_COL) * (LABEL_HEIGHT + LABEL_BOTTOM_MARGIN)) - LABEL_BOTTOM_MARGIN;
    const clusterWidth = (colCount * LABEL_WIDTH + LABEL_RIGHT_MARGIN) - LABEL_RIGHT_MARGIN;
    const labelGeometry = makeRectangleFromPoint(cluster.point, resolution, clusterWidth, clusterHeight, 0, -LABEL_BOTTOM_MARGIN*2);


    const colIndex = Math.floor(cluster.index / LABELS_PER_COL);
    const rowIndex = cluster.index % LABELS_PER_COL;
    const offsetX = -(clusterWidth / 2) + (LABEL_WIDTH / 2) + (colIndex * (LABEL_WIDTH + LABEL_RIGHT_MARGIN));
    const offsetY = -(clusterHeight / 2) + (LABEL_HEIGHT / 2) + (rowIndex * (LABEL_HEIGHT + LABEL_BOTTOM_MARGIN));

    return {
        labelGeometry,
        offsetX,
        offsetY
    };
};

const MIN_SCALE = 0.9;
const MAX_SCALE = 16;
const COEFICIENT = 0.7;
export const makeIconStyle = (icon, scale) => (rotation: number, baseResolution: number, resolution: number) => {
    const realScale = baseResolution / resolution;
    const _scale = Math.max(Math.min(Math.pow(realScale, COEFICIENT), MAX_SCALE), MIN_SCALE) * scale;

    return new Style({
        image: new Icon({
            src: typeof icon === 'function' ? icon(_scale) : icon,
            rotation: rotation,
            scale: _scale
        })
    });
};

const MIN_OFFSET = 1;
const MAX_OFFSET = 3;
export const getLabelOffsetCoefficient = (baseResolution: number, resolution: number) => {
    const scale = baseResolution / resolution;
    return Math.max(Math.min(Math.log(scale), MAX_OFFSET), MIN_OFFSET);
};

export const makeLabelStyle = (color: string, backgroundColor: string) => (text: string, offsetX?: number = 0, offsetY?: number = 0, geometry?: Geometry) => (
    new Style({
        geometry: geometry,
        text: new Text({
            font: 'bold 1.3em sans-serif',
            text: text,
            fill: new Fill({color: color}),
            backgroundFill: new Fill({color: backgroundColor}),
            backgroundStroke: new Stroke({
                color: backgroundColor,
                lineJoin: 'round',
                width: 10,
                miterLimit: 2
            }),
            offsetX: offsetX,
            offsetY: offsetY,
            padding: [-5, 5, -5, 5]
        })
    })
);

export const makeDraggableLabelStyle = (color: string, backgroundColor: string) => {
    const font = 'bold 1.3em sans-serif';
    const fill = new Fill({color: color});
    const backgroundFill = new Fill({color: backgroundColor});
    const backgroundStroke = new Stroke({
        color: backgroundColor,
        lineJoin: 'round',
        width: 10,
        miterLimit: 2
    });
    const padding = [-5, 5, -6, 6];

    return (label: string) => (
        new Style({
            text: new Text({
                font,
                text: label,
                fill,
                backgroundFill,
                backgroundStroke,
                padding
            }),
            zIndex: ZINDEX.draggableLabels
        })
    );
};

export const makeLabelConnectorStyle = (color: string) => (
    new Style({
        stroke: new Stroke({
            color
        }),
        zIndex: ZINDEX.labelConnectors
    })
);

export const getStyle = (styles, selected, dataSource) => {
    const selectionType = selected ? 'selected' : 'default';
    const style = styles[selectionType][dataSource];

    if(!style) {
        throw Error(`Undefined style for ${selectionType} ${dataSource}`);
    }

    return style;
};

export const useTargetStyle = (style, data) => {
    const baseResolution = useBaseResolution();
    const labelConfig = useLabelConfig();
    const labelsEnabled = labelConfig.enabled && labelConfig.labelType === 'normal';
    const label = data.label || 'N/A';
    const rotation = data.rotation;

    return (feature, resolution) => {
        let _labelStyle;
        if(labelsEnabled) {
            const cluster = feature.get('cluster');

            if(cluster && labelConfig.cluster) {
                const { labelGeometry, offsetX, offsetY } = getClusterLabel(cluster, resolution);

                _labelStyle = style.label(label, offsetX, offsetY, labelGeometry);
            }
            else {
                const offsetCoefficient = getLabelOffsetCoefficient(baseResolution, resolution);
                const rectangleGeometry = makeRectangleFromPoint(feature.getGeometry(), resolution, LABEL_WIDTH, LABEL_HEIGHT + LABEL_BOTTOM_MARGIN, 20 * offsetCoefficient, -30 * offsetCoefficient);

                _labelStyle = style.label(label, 0, 0, rectangleGeometry);
            }
        }

        const styles = [
            style.icon(rotation, baseResolution, resolution)
        ];

        if(_labelStyle) {
            styles.push(_labelStyle);
        }

        return styles;
    };
};

export const useDraggableLabelStyle = (style, data) => {
    const label = data.label || 'N/A';

    return useMemo(() => style(label), [style, label]);
};